import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import {
  useLegalClientSaveHeadEmail,
  useLegalClientSaveLegalHeadFirstHeadEmail,
  useLegalClientSendCorporateAccountVerificationEmailToBeneficiary,
  useLegalClientSendCorporateAccountVerificationEmailToHead,
  useLegalClientSendCorporateAccountVerificationEmailToLegalHeadFirstHead,
  useOnboardingLegalClientParticipantsLazy,
} from 'api/requests';
import { useLegalClientSaveBeneficiaryEmail } from 'api/requests/onboarding/legal/beneficiary';
import { KYCStatus } from 'api/types/data';
import {
  LegalClientEntity,
  LegalClientRepresentativeEntity,
} from 'api/types/entity';
import { ErrorCode } from 'api/types/error';
import { isFinanceProject } from 'config';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { useNotify } from 'libs/notify';
import { WaitingKycUserStatusType } from 'modules/onboarding/common/components';

import { FormErrors } from '../forms/emailForm';

export const useParticipantsWaitingKyc = (
  currentClient: LegalClientRepresentativeEntity | null,
  legalClient: LegalClientEntity | null
) => {
  const notify = useNotify();
  const { t } = useTranslation();

  const { send: sendFirstHead } =
    useLegalClientSendCorporateAccountVerificationEmailToLegalHeadFirstHead();

  const { send: sendHead } =
    useLegalClientSendCorporateAccountVerificationEmailToHead();

  const { send: sendBeneficiary } =
    useLegalClientSendCorporateAccountVerificationEmailToBeneficiary();

  const { legalClientParticipants, getLegalClientParticipants } =
    useOnboardingLegalClientParticipantsLazy();

  const {
    saveHeadEmail,
    loading: loadingSaveHeadEmail,
    error: errorSaveHeadEmail,
  } = useLegalClientSaveHeadEmail();

  const {
    saveBeneficiaryEmail,
    loading: loadingSaveBeneficiaryEmail,
    error: errorSaveBeneficiaryEmail,
  } = useLegalClientSaveBeneficiaryEmail();

  const {
    saveFirstHeadEmail,
    loading: loadingSaveFirstHeadEmail,
    error: errorSaveFirstHeadEmail,
  } = useLegalClientSaveLegalHeadFirstHeadEmail();

  const isFinance = useRef(isFinanceProject());

  const isWaitingKyc = useMemo(() => {
    if (currentClient?.kyc?.status !== KYCStatus.success) {
      return false;
    }

    const isWaitingHeadsKyc = legalClient?.heads?.some(
      ({ kyc }) => kyc?.status !== KYCStatus.success
    );

    const isWaitingFirstHeadsKyc = isFinance.current
      ? legalClient?.legalHeads?.some(({ firstHeads }) =>
          firstHeads?.some(({ kyc }) => kyc?.status !== KYCStatus.success)
        )
      : false;

    const isWaitingbeneficiariesHeadsKyc = legalClient?.beneficiaries?.some(
      ({ kyc }) => kyc?.status !== KYCStatus.success
    );

    return (
      isWaitingHeadsKyc ||
      isWaitingFirstHeadsKyc ||
      isWaitingbeneficiariesHeadsKyc
    );
  }, [
    currentClient?.kyc?.status,
    legalClient?.beneficiaries,
    legalClient?.heads,
    legalClient?.legalHeads,
  ]);

  const users = useMemo(() => {
    const participants = legalClientParticipants ?? legalClient;
    const parseStatus = (isActive: boolean, kycStatus?: KYCStatus) => {
      if (!isActive) {
        return WaitingKycUserStatusType.notStarted;
      }
      switch (kycStatus) {
        case KYCStatus.error:
          return WaitingKycUserStatusType.rejected;
        case KYCStatus.success:
          return WaitingKycUserStatusType.success;
        default:
          return WaitingKycUserStatusType.progress;
      }
    };
    return lodash
      .compact(
        participants?.heads?.map((v) => ({
          id: v.id,
          status: parseStatus(!!v.isActive, v.kyc?.status),
          name: v.name,
          email: v.email!,
        }))
      )
      .concat(
        lodash.compact(
          participants?.beneficiaries?.map((v) => ({
            id: v.id,
            status: parseStatus(!!v.isActive, v.kyc?.status),
            name: v.name,
            email: v.email!,
          }))
        )
      )
      .concat(
        lodash
          .compact(
            isFinance.current
              ? participants?.legalHeads?.map((v) =>
                  lodash.compact(
                    v.firstHeads?.map((v) => ({
                      id: v.id,
                      status: parseStatus(!!v.isActive, v.kyc?.status),
                      name: v.name,
                      email: v.email!,
                    }))
                  )
                )
              : undefined
          )
          .flat()
      );
  }, [legalClient, legalClientParticipants]);

  const onResendCode = useCallback(
    (id: string) => {
      const head = legalClient?.heads?.find((v) => v.id === id);

      if (head) {
        sendHead(legalClient?.id!, id);
        return;
      }

      const beneficiary = legalClient?.beneficiaries?.find((v) => v.id === id);

      if (beneficiary) {
        sendBeneficiary(legalClient?.id!, id);
        return;
      }

      const legalHead = legalClient?.legalHeads?.find((v) =>
        v.firstHeads?.find((v) => v.id === id)
      );

      if (legalHead) {
        sendFirstHead(legalClient!.id, legalHead.id, id);
      }
    },
    [legalClient, sendBeneficiary, sendFirstHead, sendHead]
  );

  const onEditEmail = async (id: string, email: string) => {
    const head = legalClient?.heads?.find((v) => v.id === id);

    const handleResult = (res: any) => {
      if (res) {
        notify.info(t('onboarding.waitingKyc.emailSuccessEdit'));
      }
      return !!res;
    };

    if (head) {
      const res = await saveHeadEmail(legalClient?.id!, id, email);
      return handleResult(res);
    }

    const beneficiary = legalClient?.beneficiaries?.find((v) => v.id === id);

    if (beneficiary) {
      const res = await saveBeneficiaryEmail(legalClient?.id!, id, email);
      return handleResult(res);
    }

    const legalHead = legalClient?.legalHeads?.find((v) =>
      v.firstHeads?.find((v) => v.id === id)
    );

    if (legalHead) {
      const res = await saveFirstHeadEmail(
        legalClient!.id,
        legalHead.id,
        id,
        email
      );
      return handleResult(res);
    }

    return false;
  };

  const formErrorSaveEmail = useMemo((): FormErrors | undefined => {
    const error =
      errorSaveBeneficiaryEmail ||
      errorSaveFirstHeadEmail ||
      errorSaveHeadEmail;

    if (
      error?.code === ErrorCode.ACCOUNT_ALREADY_ACTIVE ||
      error?.code === ErrorCode.USER_WITH_EMAIL_ALREADY_EXISTS
    ) {
      return { email: error?.message };
    }

    return undefined;
  }, [errorSaveBeneficiaryEmail, errorSaveFirstHeadEmail, errorSaveHeadEmail]);

  useLayoutEffect(() => {
    if (legalClient?.id && isWaitingKyc) {
      getLegalClientParticipants(legalClient.id);
    }
  }, [getLegalClientParticipants, isWaitingKyc, legalClient?.id]);

  return {
    users,
    isWaitingKyc,
    loadingSaveEmail:
      loadingSaveHeadEmail ||
      loadingSaveBeneficiaryEmail ||
      loadingSaveFirstHeadEmail,
    formErrorSaveEmail,
    onResendCode,
    getLegalClientParticipants,
    onEditEmail,
  };
};
